// services/soundService.js
class SoundService {
  constructor() {
    this.sounds = new Map();
    this.isMuted = false;
    this.volume = 0.7;
    this.audioContext = null;
    this.isAudioContextInitialized = false;
  }

  // Initialize audio context (required for some browsers)
  async initAudioContext() {
    if (this.isAudioContextInitialized) return;

    try {
      // Create audio context suspended, will be resumed on user interaction
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      this.isAudioContextInitialized = true;
    } catch (error) {
      console.warn("AudioContext not supported:", error);
    }
  }

  // Resume audio context (must be called after user interaction)
  async resumeAudioContext() {
    if (this.audioContext && this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  // Preload sounds with better error handling
  preloadSounds() {
    const soundTypes = {
      notification: "/sounds/success.mp3",
      alert: "/sounds/success.mp3",
      success: "/sounds/success.mp3",
      error: "/sounds/success.mp3",
    };

    Object.entries(soundTypes).forEach(([key, path]) => {
      try {
        const audio = new Audio();
        audio.preload = "auto";
        audio.volume = this.volume;

        // Add error handling for loading
        audio.addEventListener("error", (e) => {
          console.error(`Failed to load sound ${key}:`, e);
        });

        audio.addEventListener("canplaythrough", () => {
          console.log(`Sound ${key} loaded successfully`);
        });

        audio.src = path;
        this.sounds.set(key, audio);
      } catch (error) {
        console.error(`Error creating audio for ${key}:`, error);
      }
    });
  }

  // Play sound by type with improved error handling
  async playSound(type = "success") {
    if (this.isMuted) {
      console.log("Sound muted, skipping playback");
      return;
    }

    try {
      // Ensure audio context is initialized and resumed
      await this.initAudioContext();
      await this.resumeAudioContext();

      const sound = this.sounds.get(type);
      if (!sound) {
        console.warn(`Sound type "${type}" not found`);
        return;
      }

      // Create a new instance to allow overlapping sounds
      const soundClone = new Audio();
      soundClone.src = sound.src;
      soundClone.volume = this.volume;

      // Add event listeners for debugging
      soundClone.addEventListener("play", () => {
        console.log(`Playing sound: ${type}`);
      });

      soundClone.addEventListener("error", (e) => {
        console.error(`Sound playback error for ${type}:`, e);
      });

      soundClone.addEventListener("ended", () => {
        console.log(`Sound ended: ${type}`);
        soundClone.remove(); // Clean up
      });

      // Play the sound
      const playPromise = soundClone.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error(`Play failed for ${type}:`, error);
          // Fallback: try to play the original sound
          this.fallbackPlay(sound, type);
        });
      }
    } catch (error) {
      console.error("Error in playSound:", error);
    }
  }

  // Fallback method for playing sounds
  fallbackPlay(sound, type) {
    try {
      sound.currentTime = 0; // Reset to start
      const playPromise = sound.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error(`Fallback play also failed for ${type}:`, error);
        });
      }
    } catch (error) {
      console.error("Fallback play error:", error);
    }
  }

  // Set volume (0 to 1)
  setVolume(level) {
    this.volume = Math.max(0, Math.min(1, level));
    // Update volume for all loaded sounds
    this.sounds.forEach((sound) => {
      sound.volume = this.volume;
    });
  }

  // Mute/unmute all sounds
  toggleMute() {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  // Check if audio is supported
  isSupported() {
    return typeof Audio !== "undefined";
  }

  // Check if a specific sound is loaded
  isSoundLoaded(type) {
    const sound = this.sounds.get(type);
    return sound && sound.readyState >= 2; // HAVE_CURRENT_DATA or better
  }

  // Get loading status of all sounds
  getLoadingStatus() {
    const status = {};
    this.sounds.forEach((sound, key) => {
      status[key] = {
        loaded: sound.readyState >= 2,
        readyState: sound.readyState,
        error: sound.error,
      };
    });
    return status;
  }
}

export default new SoundService();
